import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authAPI } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Lock, Shield, Eye, EyeOff } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cxo-placement/artifacts/o33cdnbv_profile_pic.png";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await authAPI.login(loginData);
      localStorage.setItem('ratnatraya_token', response.data.access_token);
      localStorage.setItem('ratnatraya_admin', JSON.stringify(response.data.admin));
      toast.success('Welcome back!');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] flex" data-testid="admin-login-page">
      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8">
            <ArrowLeft size={18} />
            Back to Website
          </Link>

          <div className="mb-8">
            <img src={LOGO_URL} alt="Ratnatrraya" className="h-12 w-12 mb-4" />
            <h1 className="font-secondary text-3xl font-bold text-slate-900 mb-2">Client Vault</h1>
            <p className="text-slate-600">Secure access for authorized administrators only</p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Internal Access Only</p>
                <p className="text-xs text-amber-700 mt-1">
                  This portal is restricted to authorized Ratnatrraya team members.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="login-email">Email</Label>
              <Input
                id="login-email"
                type="email"
                required
                value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                placeholder="your.email@ratnatrraya.consulting"
                className="h-12 bg-white"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="login-password">Password</Label>
              <div className="relative">
                <Input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  placeholder="••••••••"
                  className="h-12 bg-white pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button 
              type="submit" 
              className="w-full h-12 bg-[#E84C3D] hover:bg-[#C0392B]"
              disabled={isLoading}
              data-testid="login-submit-btn"
            >
              <Lock size={18} className="mr-2" />
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <p className="text-xs text-slate-500 text-center mt-6">
            Need access? Contact your administrator.
          </p>
        </div>
      </div>

      {/* Right Panel - Decorative */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 items-center justify-center p-12">
        <div className="max-w-md text-center">
          <div className="w-24 h-24 rounded-2xl bg-[#E84C3D]/20 flex items-center justify-center mx-auto mb-8">
            <img src={LOGO_URL} alt="Ratnatrraya" className="h-16 w-16" />
          </div>
          <h2 className="font-secondary text-3xl font-bold text-white mb-4">
            Secure Client Management
          </h2>
          <p className="text-slate-400 leading-relaxed">
            Access your client vault to manage client information, upload documents, 
            and track engagements — all in one secure place.
          </p>
          <div className="mt-12 grid grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-2xl font-bold text-[#E84C3D]">256-bit</div>
              <div className="text-xs text-slate-500 mt-1">Encryption</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-[#E84C3D]">100%</div>
              <div className="text-xs text-slate-500 mt-1">Secure</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-[#E84C3D]">24/7</div>
              <div className="text-xs text-slate-500 mt-1">Access</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
